import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Grid,
  Stack,
  Typography,
  CircularProgress,
  CardHeader,
  useTheme,
} from '@mui/material';
import Chart from 'react-apexcharts';
import Iconify from '../../../components/iconify';
import { reportingService } from '../../../api/reporting.service';

// ----------------------------------------------------------------------

type DashboardStats = {
  activePatients: number;
  recallsDue: number;
  appointmentsThisMonth: number;
  salesToday: number;
  examsToday: number;
  totalStockValue: number;

  salesTrends: {
    month: string;
    total: number;
  }[];

  dispenseStatusSummary: {
    status: string;
    count: number;
  }[];

  dispenseRevenueSummary: {
    status: string;
    total: number | string;
  }[];
  topSellingProducts: {
    name: string;
    quantity: number;
  }[];

  nhsClaimsStatus: {
    label: string;
    value: number;
  }[];
};
export default function DashboardWidgets() {
  const theme = useTheme();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await reportingService.getDashboardStats();
        const payload = data?.nhsClaimsStatus ? data : data?.data;
        if (payload?.nhsClaimsStatus) {
          setStats({
            ...payload,
            nhsClaimsStatus: payload.nhsClaimsStatus?.map(
              (item: { label: string; value: number }) => ({
                label: item.label,
                value: Number(item.value) || 0,
              }),
            ),
          });
        } else {
          setStats(payload ?? data);
        }
      } catch (error) {
        console.error('Failed to fetch dashboard stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!stats) return null;

  const salesChartOptions = {
    chart: {
      toolbar: { show: false },
      zoom: { enabled: false },
      selection: { enabled: false },
      animations: {
        dynamicAnimation: { enabled: false },
      },
    },
    stroke: { curve: 'smooth' as const, width: 3 },
    markers: {
      size: 4,
      hover: { size: 4, sizeOffset: 0 },
    },
    states: {
      hover: { filter: { type: 'none' } },
      active: { filter: { type: 'none' } },
    },
    xaxis: {
      categories: stats.salesTrends.map((s) => s.month),
      tooltip: { enabled: false },
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: { formatter: (val: number) => `£${val.toLocaleString()}` },
    },
    colors: [theme.palette.primary.main],
  };

  const nhsClaims = stats.nhsClaimsStatus ?? [];
  const nhsTotal = nhsClaims.reduce((sum, item) => sum + item.value, 0);

  const nhsChartOptions = {
    labels: nhsClaims.map((s) => s.label),
    colors: [
      theme.palette.success.main,
      theme.palette.warning.main,
      theme.palette.error.main,
    ],
    legend: {
      position: 'bottom',
      formatter: (seriesName: string, opts?: { seriesIndex?: number }) => {
        const index =
          opts?.seriesIndex ??
          nhsClaims.findIndex((item) => item.label === seriesName);
        const count = index >= 0 ? (nhsClaims[index]?.value ?? 0) : 0;
        return `${seriesName}: ${count}`;
      },
    },
    dataLabels: {
      enabled: nhsTotal > 0,
      formatter: (val: number) => `${Number(val).toFixed(1)}%`,
    },
    plotOptions: {
      pie: {
        donut: {
          size: '75%',
          labels: {
            show: true,
            name: { show: true },
            value: {
              show: true,
              formatter: (val: string) => val,
            },
            total: {
              show: true,
              label: 'Total',
              formatter: () => String(nhsTotal),
            },
          },
        },
      },
    },
    tooltip: {
      y: {
        formatter: (val: number, opts?: { seriesIndex?: number }) => {
          const index = opts?.seriesIndex ?? 0;
          const count = nhsClaims[index]?.value ?? 0;
          const pct =
            typeof val === 'number' && !Number.isNaN(val)
              ? Number(val).toFixed(1)
              : nhsTotal > 0
                ? ((count / nhsTotal) * 100).toFixed(1)
                : '0';
          return `${count} (${pct}%)`;
        },
      },
    },
  } as any;

  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Sales Today"
            total={stats.salesToday}
            icon="eva:shopping-cart-fill"
            color="success"
            prefix="£"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Exams Completed (Today)"
            total={stats.examsToday}
            icon="eva:file-text-fill"
            color="info"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Recalls Due (This Month)"
            total={stats.recallsDue}
            icon="eva:bell-fill"
            color="warning"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Stock Value"
            total={stats.totalStockValue}
            icon="eva:cube-fill"
            color="primary"
            prefix="£"
          />
        </Grid>

        {/* Row 2: Secondary Metrics */}
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Active Patients"
            total={stats.activePatients}
            icon="eva:people-fill"
            color="primary"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Monthly Appointments"
            total={stats.appointmentsThisMonth}
            icon="eva:calendar-fill"
            color="info"
          />
        </Grid>

        {/* Charts */}

        <Grid item xs={12} md={8}>
          <Card>
            <CardHeader title="Sales Trends (Last 6 Months)" />

            <Box sx={{ p: 3, pb: 1 }}>
              <Chart
                type="line"
                series={[
                  {
                    name: 'Revenue',
                    data: stats.salesTrends.map((s) => s.total),
                  },
                ]}
                options={salesChartOptions}
                height={320}
              />
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardHeader title="NHS Claims Status" />

            <Box
              sx={{
                p: 3,
                pb: 1,
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Chart
                type="donut"
                height={320}
                series={nhsClaims?.map((item) => item.value)}
                options={nhsChartOptions}
              />
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardHeader title="Dispense Status" />

            <Box
              sx={{
                p: 3,
                pb: 1,
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              {!stats.dispenseStatusSummary?.length ? (
                <NoChartData />
              ) : (
                <Chart
                  type="donut"
                  height={320}
                  series={stats.dispenseStatusSummary.map((item) =>
                    Number(item.count),
                  )}
                  options={{
                    labels: stats.dispenseStatusSummary?.map(
                      (item) => item.status,
                    ),
                    legend: {
                      position: 'bottom',
                    },
                    dataLabels: {
                      enabled: true,
                    },
                  }}
                />
              )}
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <Card>
            <CardHeader title="Revenue by Dispense Status" />

            <Box sx={{ p: 3 }}>
              {!stats.dispenseRevenueSummary?.length ? (
                <NoChartData />
              ) : (
                <Chart
                  type="bar"
                  height={320}
                  series={[
                    {
                      name: 'Revenue',
                      data: stats.dispenseRevenueSummary.map((item) =>
                        Number(item.total),
                      ),
                    },
                  ]}
                  options={{
                    chart: {
                      toolbar: {
                        show: false,
                      },
                    },
                    xaxis: {
                      categories: stats.dispenseRevenueSummary?.map(
                        (item) => item.status,
                      ),
                    },
                    dataLabels: {
                      enabled: false,
                    },
                    tooltip: {
                      y: {
                        formatter: (value: number) => `£${value.toFixed(2)}`,
                      },
                    },
                  }}
                />
              )}
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardHeader title="Top Selling Products" />

            <Box sx={{ p: 3 }}>
              {!stats.topSellingProducts?.length && <NoChartData />}
              {stats.topSellingProducts?.map((item) => (
                <Stack
                  key={item.name}
                  direction="row"
                  justifyContent="space-between"
                  sx={{ mb: 2 }}
                >
                  <Typography>{item.name}</Typography>

                  <Typography fontWeight="bold">{item.quantity}</Typography>
                </Stack>
              ))}
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

// ----------------------------------------------------------------------

function NoChartData() {
  return (
    <Typography
      variant="body2"
      color="text.secondary"
      sx={{ py: 6, width: 1, textAlign: 'center' }}
    >
      No data available
    </Typography>
  );
}

// ----------------------------------------------------------------------

type SummaryCardProps = {
  title: string;
  total: number;
  icon: string;
  color: string;
  prefix?: string;
  suffix?: string;
};

function SummaryCard({
  title,
  total,
  icon,
  color,
  prefix = '',
  suffix = '',
}: SummaryCardProps) {
  return (
    <Card sx={{ p: 3, textAlign: 'center' }}>
      <Stack spacing={1}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 1,
            bgcolor: (theme: any) => theme.palette[color].lighter,
            color: (theme: any) => theme.palette[color].dark,
          }}
        >
          <Iconify icon={icon} width={24} height={24} />
        </Box>

        <Typography variant="h3">
          {prefix}
          {total.toLocaleString()}
          {suffix}
        </Typography>

        <Typography variant="subtitle2" color="text.secondary">
          {title}
        </Typography>
      </Stack>
    </Card>
  );
}
